import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode } from 'react'
import { patients as api } from '@/api/endpoints'
import { ApiError } from '@/api/errors'
import { queryKeys } from '@/api/queryKeys'
import type { BloodGroup, PatientResponse, PatientWriteRequest, Sex } from '@/api/types'
import { Banner } from '@/components/Banner'
import { Card } from '@/components/Card'
import { DateField } from '@/components/DateField'
import { Field } from '@/components/Field'
import { PhoneField } from '@/components/PhoneField'
import { Select } from '@/components/Select'
import { SegmentedControl } from '@/components/SegmentedControl'
import { TextArea } from '@/components/TextArea'
import { TextField } from '@/components/TextField'
import { useToast } from '@/components/Toast'
import { BLOOD_GROUP_OPTIONS, SEX_OPTIONS } from '@/lib/clinical'
import { todayIso } from '@/lib/dates'
import { looksLikePhone } from '@/lib/phone'
import { errorMessage, fieldMessages } from '@/api/messages'

function blank(): PatientWriteRequest {
  return {
    op_number: '',
    full_name: '',
    date_of_birth: null,
    age_years: null,
    sex: 'unknown',
    phone: null,
    address: null,
    blood_group: null,
  }
}

/**
 * The registration form's state, validation and save — with no opinion about
 * whether it is being shown as a page or inside a dialog. Both shells call this
 * and render {@link PatientFormFields}, so there is one form, not two that drift.
 */
export function usePatientForm({
  mode,
  patientId = '',
  seed,
  onSaved,
}: {
  mode: 'create' | 'edit'
  patientId?: string
  seed?: string | undefined
  onSaved: (patient: PatientResponse) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const [form, setForm] = useState<PatientWriteRequest>(() => {
    const base = blank()
    // Carry whatever was typed into search: digits are a phone, letters a name.
    // Never make someone retype what they already told the app.
    const typed = seed?.trim()
    if (!typed) return base
    return looksLikePhone(typed) ? { ...base, phone: typed } : { ...base, full_name: typed }
  })
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const existing = useQuery({
    queryKey: queryKeys.patient(patientId),
    queryFn: ({ signal }) => api.get(patientId, signal),
    enabled: mode === 'edit' && Boolean(patientId),
  })

  useEffect(() => {
    const patient = existing.data
    if (!patient) return
    setForm({
      op_number: patient.op_number,
      full_name: patient.full_name,
      date_of_birth: patient.date_of_birth,
      age_years: patient.date_of_birth ? null : patient.age,
      sex: patient.sex,
      phone: patient.phone,
      address: patient.address,
      blood_group: patient.blood_group,
    })
  }, [existing.data])

  /** A duplicate is usually a family sharing a number, so this warns and never
   *  blocks — but a genuine double-registration is common enough to surface. */
  const duplicates = useQuery({
    queryKey: ['duplicate-check', form.phone],
    queryFn: () =>
      api.duplicateCheck(patientId || '00000000-0000-0000-0000-000000000000', form.phone!),
    enabled: mode === 'create' && Boolean(form.phone && looksLikePhone(form.phone)),
  })

  const save = useMutation({
    mutationFn: () => {
      // Built field by field rather than spread: the generated optional
      // properties are `T | null` and exactOptionalPropertyTypes will not let
      // an `undefined` through, which is the right constraint to keep.
      const payload: PatientWriteRequest = {
        op_number: form.op_number.trim(),
        full_name: form.full_name.trim(),
        sex: form.sex ?? 'unknown',
        phone: form.phone?.trim() || null,
        address: form.address?.trim() || null,
        blood_group: form.blood_group ?? null,
        // One or the other, decided by what was filled in rather than by a
        // mode toggle the user has to find and understand.
        date_of_birth: form.date_of_birth ?? null,
        age_years: form.date_of_birth ? null : (form.age_years ?? null),
      }
      return mode === 'edit' ? api.update(patientId, payload) : api.register(payload)
    },
    onSuccess: async (patient) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.patient(patient.id) })
      await queryClient.invalidateQueries({ queryKey: ['patients'] })
      toast.show(mode === 'edit' ? 'Patient updated' : 'Patient registered')
      onSaved(patient)
    },
    onError: (caught) => {
      if (caught instanceof ApiError) {
        setError(errorMessage(caught))
        setFieldErrors(fieldMessages(caught))
      } else {
        setError('Could not save. Your work is still here — try again.')
      }
    },
  })

  function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setFieldErrors({})
    save.mutate()
  }

  return { form, setForm, error, fieldErrors, duplicates, save, submit, loading: existing.isLoading }
}

export type PatientFormState = ReturnType<typeof usePatientForm>

/** A card on a page, a plain headed block inside a dialog — a dialog is already
 *  a raised surface, and a card inside one is a box inside a box. */
function Group({
  title,
  chrome,
  children,
}: {
  title: string
  chrome: 'card' | 'plain'
  children: ReactNode
}) {
  if (chrome === 'card') return <Card title={title}>{children}</Card>
  return (
    <section>
      <h3 className="mb-3 text-2xs font-semibold tracking-wide text-fg-subtle uppercase">
        {title}
      </h3>
      {children}
    </section>
  )
}

export function PatientFormFields({
  state,
  chrome = 'card',
  autoFocus = false,
}: {
  state: PatientFormState
  chrome?: 'card' | 'plain'
  autoFocus?: boolean
}) {
  const { form, setForm, fieldErrors, duplicates } = state

  return (
    <>
      <Group title="Who they are" chrome={chrome}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label="OP number"
            value={form.op_number}
            onChange={(e) => setForm({ ...form, op_number: e.target.value })}
            error={fieldErrors.op_number}
            hint="The number the clinic writes on their sheet."
            required
            autoFocus={autoFocus}
          />
          <TextField
            label="Full name"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            error={fieldErrors.full_name}
            autoCapitalize="words"
            required
          />

          {/* Date of birth is preferred and age is the fallback: a record that
              says 34 is wrong a year later, so the server stores when an age was
              taken and ages it forward. Filling one rules out the other — no
              mode toggle to find. */}
          <DateField
            label="Date of birth"
            max={todayIso()}
            value={form.date_of_birth ?? ''}
            onChange={(iso) => setForm({ ...form, date_of_birth: iso || null, age_years: null })}
            error={fieldErrors.date_of_birth}
          />
          <TextField
            label="Age"
            type="number"
            inputMode="numeric"
            min={0}
            max={130}
            value={form.age_years ?? ''}
            disabled={Boolean(form.date_of_birth)}
            onChange={(e) =>
              setForm({ ...form, age_years: e.target.value === '' ? null : Number(e.target.value) })
            }
            error={fieldErrors.age_years}
            hint="If the date of birth is not known."
          />

          <Field label="Sex">
            {() => (
              <SegmentedControl
                label="Sex"
                options={SEX_OPTIONS}
                value={(form.sex ?? 'unknown') as Sex}
                onChange={(sex) => setForm({ ...form, sex })}
              />
            )}
          </Field>
          <Field label="Blood group">
            {({ id, labelId }) => (
              <Select
                id={id}
                labelledBy={labelId}
                placeholder="Not recorded"
                options={BLOOD_GROUP_OPTIONS}
                value={form.blood_group ?? ''}
                onChange={(group) =>
                  setForm({ ...form, blood_group: (group || null) as BloodGroup | null })
                }
              />
            )}
          </Field>
        </div>
      </Group>

      <Group title="How to reach them" chrome={chrome}>
        {duplicates.data && duplicates.data.data.length > 0 && (
          <div className="mb-4">
            <Banner tone="warning">
              {duplicates.data.data.length === 1
                ? '1 patient already has this phone number.'
                : `${duplicates.data.data.length} patients already have this phone number.`}{' '}
              Families often share one — continue if this is a different person.
            </Banner>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <PhoneField
            label="Phone number"
            value={form.phone ?? ''}
            onChange={(phone) => setForm({ ...form, phone })}
            error={fieldErrors.phone}
            hint="Any format. Spaces, dashes and +91 are all fine."
          />
        </div>
        <div className="mt-4">
          <TextArea
            label="Address"
            rows={2}
            value={form.address ?? ''}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
          />
        </div>
      </Group>
    </>
  )
}
